import { useOthers, useSelf } from "@liveblocks/react"
import type { RepositoryAuthor } from "@prismicio/editor-fields"
import { isDefined } from "@prismicio/editor-support/Type"
import {
  Avatar,
  Box,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  ErrorBoundary,
  Text,
  theme,
  Tooltip,
} from "@prismicio/editor-ui"
import { Suspense } from "react"

import { useCollaborationRoomFlag } from "../../app/integrations/AmplitudeExperiments"
import { useRepository } from "../../app/services/repository"
import { getAuthorDisplayName } from "../../documents/DocumentListBody/ActionBar/utils"

export function CollaborationAvatarStack() {
  const isCollaborationEnabled = useCollaborationRoomFlag() === "on"

  if (!isCollaborationEnabled) return null

  // We create a new error boundary and suspense block to avoid
  // breaking or having other features await the loading of the
  // CollaborationAvatarStack component data.
  return (
    <ErrorBoundary>
      <Suspense>
        <AvatarStack />
      </Suspense>
    </ErrorBoundary>
  )
}

function AvatarStack() {
  const user = useSelf()
  const othersIds = useOthers((others) => others.map((other) => other.id))
  const { authors } = useRepository()

  const userIds = [user?.id, ...othersIds].filter(isDefined)
  const onlineAuthors = authors.filter((author) => userIds.includes(author.id))

  const visibleAuthors = onlineAuthors.slice(0, maximumAmountOfShownAvatars)
  const hiddenAuthors = onlineAuthors.slice(maximumAmountOfShownAvatars)

  const hasHiddenAuthors = onlineAuthors.length > maximumAmountOfShownAvatars

  return (
    <Box
      alignItems="center"
      // follow button height to align with the document header buttons
      height={theme.size.large}
    >
      {visibleAuthors.map((author, index) => (
        <Box
          key={author.id}
          margin={{
            left: index === 0 ? 0 : "-6",
          }}
        >
          <Tooltip
            content={getTooltipContent({
              author,
              isSelf: author.id === user?.id,
            })}
            variant="custom"
            sideOffset={7}
            stableMount
          >
            <Avatar
              firstName={author.firstname}
              lastName={author.lastname}
              key={author.id}
              imageUrl={author.uploadedAvatar}
              size={28}
              border={getAvatarBorderColor(index)}
            />
          </Tooltip>
        </Box>
      ))}

      {hasHiddenAuthors && (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Box
              margin={{
                left: "-6",
              }}
            >
              <Avatar
                fallback={`+${onlineAuthors.length - maximumAmountOfShownAvatars}`}
                size={28}
                border="grey2"
              />
            </Box>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" maxHeight={256} minWidth={256}>
            <DropdownMenuLabel>
              <Text color="grey11">Users on page</Text>
            </DropdownMenuLabel>
            {hiddenAuthors.map((author, index) => (
              <DropdownMenuItem key={author.id}>
                <Box gap={6} alignItems="center">
                  <Avatar
                    firstName={author.firstname}
                    lastName={author.lastname}
                    imageUrl={author.uploadedAvatar}
                    size={16}
                    border={getAvatarBorderColor(index)}
                  />
                  <Text>{getAuthorDisplayName(author)}</Text>
                </Box>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </Box>
  )
}

function getAvatarBorderColor(index: number) {
  return availableColors[index % availableColors.length] ?? "purple9"
}

interface GetTooltipContentArgs {
  author: RepositoryAuthor
  isSelf: boolean
}

function getTooltipContent(args: GetTooltipContentArgs) {
  const { author, isSelf } = args
  const authorName = getAuthorDisplayName(author)

  return (
    <Box gap={2}>
      <Text variant="smallBold" color="white">
        {isSelf ? "You" : authorName}
      </Text>
      {isSelf && (
        <Text variant="small" color="white">
          ({authorName})
        </Text>
      )}
    </Box>
  )
}

const maximumAmountOfShownAvatars = 4
const availableColors = [
  "indigo9",
  "purple9",
  "green9",
  "amber9",
  "grey9",
] as const
